import { useCallback, useEffect, useState } from "react";
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router-dom";
import { endpoints, isTerminal } from "../api.js";
import { Icon } from "../components/Icon.jsx";
import { REPORT_FORMATS, openReport } from "../components/ReportDialog.jsx";
import { Banner, EmptyState, ErrorBanner, Loading, Spinner, StatusPill } from "../components/ui.jsx";
import { useDocumentTitle, useErrorMessage, useLocale, useLocalePath } from "../i18n/hooks.js";
import { useSession } from "../session.jsx";
import { useWorkspace } from "../workspace.jsx";

function Reports({ investigationId, jobs }) {
  const locale = useLocale();
  const errMsg = useErrorMessage();
  const reportable = jobs.filter((j) => ["succeeded", "partial"].includes(j.status));
  const [reports, setReports] = useState(null);
  const [jobId, setJobId] = useState(reportable[0]?.id || "");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    endpoints.reports(investigationId).then((d) => setReports(d.reports || []), setError);
  }, [investigationId]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!jobId && reportable[0]) setJobId(reportable[0].id);
  }, [jobId, reportable]);

  const generate = async (format) => {
    setBusy(format);
    setError(null);
    try {
      const { report } = await endpoints.createReport({ investigationId, jobId, format, locale });
      setReports((r) => [report, ...(r || [])]);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  const download = async (id) => {
    setError(null);
    try {
      await openReport(id);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h3">
          <FormattedMessage id="reports.title" />
        </h2>
      </div>
      {reportable.length ? (
        <div className="report-gen">
          <label className="field field-inline">
            <span className="field-label">
              <FormattedMessage id="reports.forJob" />
            </span>
            <select value={jobId} onChange={(e) => setJobId(e.target.value)}>
              {reportable.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.id.slice(0, 8)} · {new Date(j.createdAt).toLocaleString(locale)}
                </option>
              ))}
            </select>
          </label>
          <div className="btn-row">
            {REPORT_FORMATS.map((f) => (
              <button
                key={f}
                type="button"
                className="btn btn-outline btn-sm"
                disabled={Boolean(busy) || !jobId}
                onClick={() => generate(f)}
              >
                {busy === f ? <Spinner size={14} /> : <Icon name="file" size={14} />}
                <FormattedMessage id="reports.generateFormat" values={{ format: f.toUpperCase() }} />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="muted small">
          <FormattedMessage id="reports.needsJob" />
        </p>
      )}
      <ErrorBanner message={error ? errMsg(error) : null} />
      {reports && reports.length ? (
        <ul className="report-list">
          {reports.map((r) => (
            <li key={r.id}>
              <span className="file-badge">{String(r.format).toUpperCase()}</span>
              <span className="muted small mono">{r.jobId?.slice(0, 8)}</span>
              <span className="muted small grow">
                <FormattedDate value={r.createdAt} dateStyle="medium" timeStyle="short" />
              </span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => download(r.id)}>
                <Icon name="download" size={14} />
                <FormattedMessage id="reports.download" />
              </button>
            </li>
          ))}
        </ul>
      ) : reports ? (
        <p className="muted small">
          <FormattedMessage id="reports.empty" />
        </p>
      ) : null}
    </section>
  );
}

export default function InvestigationDetail() {
  const { id } = useParams();
  const intl = useIntl();
  const lp = useLocalePath();
  const navigate = useNavigate();
  const errMsg = useErrorMessage();
  const { refreshTenant } = useSession();
  const { trackJob } = useWorkspace();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [runError, setRunError] = useState(null);
  const [running, setRunning] = useState(false);
  useDocumentTitle(data ? null : "investigation.title");

  const load = useCallback(() => {
    setError(null);
    endpoints.investigation(id).then(setData, setError);
  }, [id]);
  useEffect(load, [load]);
  useEffect(() => {
    if (data) document.title = `${data.investigation.name} · ${intl.formatMessage({ id: "brand.name" })}`;
  }, [data, intl]);

  const rerun = async () => {
    setRunning(true);
    setRunError(null);
    try {
      const { job } = await endpoints.startJob(id);
      trackJob(job, data.investigation);
      refreshTenant();
      navigate(lp(`/jobs/${job.id}`));
    } catch (e) {
      setRunError(e);
      setRunning(false);
    }
  };

  if (error) {
    return (
      <div className="page">
        <ErrorBanner message={errMsg(error)} onRetry={load} />
      </div>
    );
  }
  if (!data) return <div className="page"><Loading /></div>;

  const { investigation: inv, jobs = [] } = data;
  const sortedJobs = [...jobs].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  const active = sortedJobs.some((j) => !isTerminal(j.status));

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <Link to={lp("/investigations")} className="back-link">
            <Icon name="arrowLeft" size={15} />
            <FormattedMessage id="nav.investigations" />
          </Link>
          <h1 className="h1">{inv.name}</h1>
          <p className="meta-line">
            <span className={`type-badge type-${inv.targetType}`}>
              <FormattedMessage id={`targetType.${inv.targetType}`} defaultMessage={inv.targetType} />
            </span>
            <span className="mono">{inv.target}</span>
            <span className="tier-tag">{inv.tier}</span>
            <StatusPill ns="invStatus" value={inv.status} />
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={rerun} disabled={running || active}>
          {running ? <Spinner size={16} /> : <Icon name="play" size={15} />}
          <FormattedMessage id={jobs.length ? "investigation.rerun" : "investigation.run"} />
        </button>
      </header>
      <ErrorBanner message={runError ? errMsg(runError) : null} />

      <div className="detail-grid">
        <section className="card">
          <div className="card-head">
            <h2 className="h3">
              <FormattedMessage id="investigation.jobs" />
            </h2>
          </div>
          {sortedJobs.length === 0 ? (
            <EmptyState icon="play" body={<FormattedMessage id="investigation.noJobs" />} />
          ) : (
            <div className="grid-wrap flush">
              <table className="grid">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage id="investigation.col.job" />
                    </th>
                    <th>
                      <FormattedMessage id="investigation.col.status" />
                    </th>
                    <th>
                      <FormattedMessage id="investigation.col.cost" />
                    </th>
                    <th>
                      <FormattedMessage id="investigation.col.started" />
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sortedJobs.map((j) => (
                    <tr key={j.id} className="is-clickable" onClick={() => navigate(lp(`/jobs/${j.id}`))}>
                      <td className="mono">{j.id.slice(0, 8)}</td>
                      <td>
                        <StatusPill ns="jobStatus" value={j.status} />
                      </td>
                      <td className="mono">
                        {j.actualCost != null ? (
                          <FormattedNumber value={j.actualCost} />
                        ) : (
                          <span className="muted">
                            ≤ <FormattedNumber value={j.estCost} />
                          </span>
                        )}
                      </td>
                      <td className="muted nowrap">
                        <FormattedDate value={j.createdAt} dateStyle="medium" timeStyle="short" />
                      </td>
                      <td className="end-text">
                        <Link to={lp(`/jobs/${j.id}`)} className="link-sm" onClick={(e) => e.stopPropagation()}>
                          <FormattedMessage id={isTerminal(j.status) ? "investigation.openResults" : "investigation.openLive"} />
                          <Icon name="chevronRight" size={14} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="card">
          <h2 className="h3">
            <FormattedMessage id="investigation.details" />
          </h2>
          <dl className="kv">
            <div className="kv-row">
              <dt>
                <FormattedMessage id="investigation.purpose" />
              </dt>
              <dd>{intl.formatMessage({ id: `purpose.${inv.purpose}`, defaultMessage: inv.purpose })}</dd>
            </div>
            <div className="kv-row">
              <dt>
                <FormattedMessage id="investigation.purposeText" />
              </dt>
              <dd className="prewrap">{inv.purposeText}</dd>
            </div>
            {inv.legalBasis ? (
              <div className="kv-row">
                <dt>
                  <FormattedMessage id="investigation.legalBasis" />
                </dt>
                <dd className="prewrap">{inv.legalBasis}</dd>
              </div>
            ) : null}
            <div className="kv-row">
              <dt>
                <FormattedMessage id="investigation.created" />
              </dt>
              <dd>
                <FormattedDate value={inv.createdAt} dateStyle="long" timeStyle="short" />
              </dd>
            </div>
          </dl>
          <Banner kind="info" icon="shield">
            <FormattedMessage id="investigation.auditNote" />
          </Banner>
        </aside>
      </div>

      <Reports investigationId={inv.id} jobs={sortedJobs} />
    </div>
  );
}
